import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Form,
  Input,
  Modal,
  message,
  Upload,
  Select,
  InputNumber,
  Switch,
  Row,
  Col,
  Image,
  Popconfirm,
  ConfigProvider
} from 'antd';
import zhCN from 'antd/locale/zh_CN';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  UploadOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { carouselService, CarouselItem } from '../../../../../services/carouselService';
import { PAGINATION_CONFIG } from '../../components/PaginationConfig';
import QueryArea from '../../components/QueryArea';

const { Option } = Select;
const { TextArea } = Input;

const CarouselManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [carouselList, setCarouselList] = useState<CarouselItem[]>([]);
  const [editingCarousel, setEditingCarousel] = useState<CarouselItem | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [form] = Form.useForm();
  const [currentImageData, setCurrentImageData] = useState<string>('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState({
    title: '',
    subtitle: '',
  });

  // 查询字段配置
  const queryFields = [
    {
      key: 'title',
      label: '标题',
      type: 'input' as const,
      placeholder: '请输入标题'
    },
    {
      key: 'subtitle',
      label: '副标题',
      type: 'input' as const,
      placeholder: '请输入副标题'
    }
  ];

  // 加载轮播图片列表 - 使用useCallback优化
  const loadCarouselList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        pageNum: pagination.current,
        pageSize: pagination.pageSize,
        ...searchParams
      };
      const response = await carouselService.getList(params);
      setCarouselList(response.records || []);
      setPagination(prev => ({
        ...prev,
        current: response.current || prev.current,
        total: response.total || 0,
      }));
    } catch (error) {
      console.error('加载轮播图片列表失败:', error);
      message.error('加载轮播图片列表失败');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, searchParams]);

  // 初始加载和搜索参数或分页变化时重新加载
  useEffect(() => {
    loadCarouselList();
  }, [loadCarouselList]);

  // 处理搜索
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 处理重置
  const handleReset = () => {
    setSearchParams({
      title: '',
      subtitle: '',
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 处理搜索参数变化
  const handleSearchParamsChange = useCallback((values: Record<string, any>) => {
    setSearchParams({
      title: values.title || '',
      subtitle: values.subtitle || '',
    });
  }, []);

  // 处理分页变化
  const handlePaginationChange = (page: number, pageSize?: number) => {
    setPagination(prev => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
  };

  // 处理新增
  const handleAdd = () => {
    setEditingCarousel(null);
    form.resetFields();
    setCurrentImageData('');
    setModalVisible(true);
  };

  // 处理编辑
  const handleEdit = (record: CarouselItem) => {
    setEditingCarousel(record);
    form.setFieldsValue(record);
    setCurrentImageData(record.imageData || '');
    setModalVisible(true);
  };

  // 处理删除
  const handleDelete = async (id: number) => {
    try {
      await carouselService.delete([id]);
      message.success('删除成功');
      loadCarouselList();
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  // 处理批量删除
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的记录');
      return;
    }
    try {
      await carouselService.delete(selectedRowKeys as number[]);
      message.success('批量删除成功');
      setSelectedRowKeys([]);
      loadCarouselList();
    } catch (error) {
      console.error('批量删除失败:', error);
      message.error('批量删除失败');
    }
  };

  // 处理保存
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingCarousel) {
        await carouselService.update({ ...editingCarousel, ...values });
        message.success('更新成功');
      } else {
        await carouselService.create(values);
        message.success('新增成功');
      }
      setModalVisible(false);
      loadCarouselList();
    } catch (error) {
      console.error('保存失败:', error);
      message.error('保存失败');
    }
  };

  // 处理图片上传
  const handleImageUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      form.setFieldsValue({
        imageData: result,
        imageType: file.type
      });
      setCurrentImageData(result);
    };
    reader.readAsDataURL(file);
    return false; // 阻止默认上传
  };

  // 预览图片
  const handlePreview = (imageData: string) => {
    setPreviewImage(imageData);
    setPreviewVisible(true);
  };

  // 表格列定义
  const columns: ColumnsType<CarouselItem> = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: '副标题',
      dataIndex: 'subtitle',
      key: 'subtitle',
      ellipsis: true,
    },
    {
      title: '操作',
      key: 'action',
      width: 140,
      align: 'center',
      fixed: 'right' as const,
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="primary" 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button 
            danger 
            size="small" 
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id!)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ width: '100%', overflow: 'hidden' }}>
      <Card style={{ width: '100%' }}>
        {/* 统一查询区域 */}
        <QueryArea
          fields={queryFields}
          values={searchParams}
          onValuesChange={handleSearchParamsChange}
          onAdd={handleAdd}
          onBatchDelete={handleBatchDelete}
          batchDeleteDisabled={selectedRowKeys.length === 0}
          selectedCount={selectedRowKeys.length}
          addButtonText="新增轮播图"
          batchDeleteButtonText="批量删除"
        />

        <Table
          columns={columns}
          dataSource={carouselList}
          loading={loading}
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            ...PAGINATION_CONFIG.simple,
            onChange: handlePaginationChange,
            onShowSizeChange: handlePaginationChange,
          }}
          scroll={{ x: 'max-content' }}
          size="small"
        />
      </Card>

      {/* 编辑模态框 */}
      <Modal
        title={editingCarousel ? '编辑轮播图' : '新增轮播图'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          setCurrentImageData('');
        }}
        width={800}
        okText="保存"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 20 }}
          initialValues={{
            status: '1',
            sortOrder: 0,
            imageType: 'image/jpeg'
          }}
        >
          {/* 第一排：标题 */}
          <Form.Item
            name="title"
            label="标题:"
            rules={[{ required: true, message: '请输入标题' }]}
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
            labelAlign="left"
          >
            <Input placeholder="请输入标题" />
          </Form.Item>

          {/* 第二排：副标题 */}
          <Form.Item
            name="subtitle"
            label="副标题:"
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
            labelAlign="left"
          >
            <Input placeholder="请输入副标题" />
          </Form.Item>

          {/* 第三排：描述 */}
          <Form.Item
            name="description"
            label="描述:"
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
            labelAlign="left"
          >
            <TextArea rows={3} placeholder="请输入描述" />
          </Form.Item>

          {/* 第四排：排序和状态 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="sortOrder"
                label="排序:"
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
                labelAlign="left"
              >
                <InputNumber min={0} placeholder="排序号" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="状态:"
                valuePropName="checked"
                getValueFromEvent={(checked) => checked ? '1' : '0'}
                getValueProps={(value) => ({ checked: value === '1' })}
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
                labelAlign="left"
              >
                <Switch checkedChildren="启用" unCheckedChildren="禁用" />
              </Form.Item>
            </Col>
          </Row>

          {/* 第五排：备注 */}
          <Form.Item
            name="remark"
            label="备注:"
            labelCol={{ span: 4 }}
            wrapperCol={{ span: 20 }}
            labelAlign="left"
          >
            <Input placeholder="请输入备注" />
          </Form.Item>

          {/* 第六排：图片和图片类型 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="imageData"
                label="图片:"
                rules={[{ required: true, message: '请上传图片' }]}
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
                labelAlign="left"
              >
                <Upload
                  beforeUpload={handleImageUpload}
                  showUploadList={false}
                  accept="image/*"
                >
                  <Button icon={<UploadOutlined />}>上传图片</Button>
                </Upload>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="imageType"
                label="图片类型:"
                labelCol={{ span: 6 }}
                wrapperCol={{ span: 18 }}
                labelAlign="left"
              >
                <Select placeholder="选择图片类型">
                  <Option value="image/jpeg">JPEG</Option>
                  <Option value="image/png">PNG</Option>
                  <Option value="image/gif">GIF</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* 图片预览（单独一行，放在最下方） */}
          {currentImageData && (
            <Form.Item
              label=" "
              labelCol={{ span: 4 }}
              wrapperCol={{ span: 20 }}
            >
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <ConfigProvider locale={zhCN}>
                  <Image
                    width={200}
                    height={120}
                    src={currentImageData}
                    style={{ objectFit: 'cover', borderRadius: 4 }}
                    preview={{
                      mask: '预览'
                    }}
                  />
                </ConfigProvider>
                <Button
                  type="text"
                  danger
                  size="small"
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    background: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: 24,
                    height: 24,
                    minWidth: 24,
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onClick={() => {
                    form.setFieldsValue({ imageData: undefined });
                    setCurrentImageData('');
                  }}
                >
                  ×
                </Button>
              </div>
            </Form.Item>
          )}

        </Form>
      </Modal>

      {/* 图片预览模态框 */}
      <Modal
        title="图片预览"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={800}
      >
        <ConfigProvider locale={zhCN}>
          <Image
            src={previewImage}
            style={{ width: '100%' }}
            preview={{
              mask: '预览'
            }}
          />
        </ConfigProvider>
      </Modal>
    </div>
  );
};

export default CarouselManagement;
